// src/components/KnowledgeBoard.js — /knowledge-board, what an agent reads
// before answering: documents and web pages, titled and summarised by the AI.
// Header (agent scope, add menu) → « À vérifier » (sources the agent cannot
// read: knowledge_index.status from the re-indexing sweep, KI-185) → tiles
// (sources, PDF, texts and web pages, unreadable — they filter) → the sources
// table. URL: ?agent=<agent _id> (the agent page links here) or ?agent=none
// (« Sans agent »: the files stored before per-agent scoping, without
// assistant_id, and those of a deleted agent — read from the account's
// list), ?q=, ?filter=.
// API: GET /api/users/user, /api/ai-assistants, /api/files/list?assistant_id=
// (the agent's sources) and /api/files/list (the account's, for the counts per
// agent), /api/files/generate-view-url; POST /api/files/upload,
// /api/files/upload-url-file; PUT /api/files/update-web-json-2/,
// /api/files/delete-web-json-2/.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BookOpen, ChevronDown, CircleAlert, ExternalLink, Eye, File, FileText, Globe, Link as LinkIcon, Plus, Search,
  Trash2, TriangleAlert, Upload, X,
} from 'lucide-react';
import {
  Badge, Banner, Card, ConfirmDialog, EmptyState, Field, IconChip, Kpi, Modal, OverflowMenu, PageHeader, Segmented,
  Toast,
} from '../design/ui';
import {
  KNOWLEDGE_FILTERS, UNREADABLE_STATUSES, authHeaders, countByAgent, fileExtension, fileKind, formatCount,
  formatPercent, indexStatus, knowledgeSummary, matchesKnowledgeFilter,
} from './aiAgentsShared';
import './KnowledgeBoard.css';

const SEGMENT_FILTERS = ['all', 'pdf', 'text', 'other'];
// ?agent=none: the sources attached to no existing agent (no assistant_id,
// or the id of an agent since deleted).
export const NO_AGENT = 'none';
export const withoutAgent = (file, agentIds) => !file.assistant_id || !agentIds.has(file.assistant_id);
const KIND_ICON = { pdf: FileText, text: Globe, other: File };

const KnowledgeBoard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const agentParam = searchParams.get('agent');
  const query = searchParams.get('q') || '';
  const filter = KNOWLEDGE_FILTERS.includes(searchParams.get('filter')) ? searchParams.get('filter') : 'all';

  const [user, setUser] = useState(undefined);
  const [assistants, setAssistants] = useState(null); // null = loading
  const [resources, setResources] = useState(null); // null = loading
  const [resourcesError, setResourcesError] = useState(null);
  const [counts, setCounts] = useState(null);
  const [toast, setToast] = useState(null);

  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState(null);
  const [addingUrl, setAddingUrl] = useState(false);
  const [uploadStatuses, setUploadStatuses] = useState({});
  const [viewer, setViewer] = useState(null); // { url, textContent, resource }
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const addMenuRef = useRef(null);
  const fileInputRef = useRef(null);
  const urlFileInputRef = useRef(null);

  const updateParams = useCallback((updates) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') next.delete(key);
        else next.set(key, String(value));
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const showToast = (tone, text) => setToast({ tone, text });
  useEffect(() => {
    if (!toast || toast.tone === 'danger') return undefined;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  // The add menu closes on an outside click or Escape.
  useEffect(() => {
    if (!showAddMenu) return undefined;
    const onDown = (event) => { if (addMenuRef.current && !addMenuRef.current.contains(event.target)) setShowAddMenu(false); };
    const onKey = (event) => { if (event.key === 'Escape') setShowAddMenu(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [showAddMenu]);

  useEffect(() => {
    if (!localStorage.getItem('access_token')) {
      navigate('/');
      return;
    }
    axios.get('/api/users/user', { headers: authHeaders() })
      .then((response) => setUser(response.data))
      .catch((error) => {
        console.error('Failed to fetch user data:', error);
        setUser(null);
      });
    axios.get('/api/ai-assistants', { headers: authHeaders() })
      .then((response) => setAssistants(Array.isArray(response.data) ? response.data : []))
      .catch((error) => {
        console.error('Failed to fetch assistants:', error);
        setAssistants([]);
      });
  }, [navigate]);

  // The agent in the URL (or « Sans agent »), else the default agent, else the first one.
  const selectedAssistantId = useMemo(() => {
    if (!assistants || !assistants.length) return null;
    if (agentParam === NO_AGENT) return NO_AGENT;
    if (agentParam && assistants.some((assistant) => assistant._id === agentParam)) return agentParam;
    const running = assistants.find((assistant) => assistant.is_running);
    return (running || assistants[0])._id;
  }, [assistants, agentParam]);

  useEffect(() => {
    if (selectedAssistantId && selectedAssistantId !== agentParam) updateParams({ agent: selectedAssistantId });
  }, [selectedAssistantId, agentParam, updateParams]);

  const noAgent = selectedAssistantId === NO_AGENT;
  const agentIds = useMemo(() => new Set((assistants || []).map((assistant) => assistant._id)), [assistants]);

  const fetchResources = useCallback(async () => {
    if (!selectedAssistantId) return;
    setResourcesError(null);
    try {
      if (selectedAssistantId === NO_AGENT) {
        const response = await axios.get('/api/files/list', { headers: authHeaders() });
        setResources(((response.data && response.data.files) || []).filter((file) => withoutAgent(file, agentIds)));
        return;
      }
      const response = await axios.get(`/api/files/list?assistant_id=${selectedAssistantId}`, { headers: authHeaders() });
      setResources((response.data && response.data.files) || []);
    } catch (error) {
      console.error('Failed to fetch resources:', error);
      setResources([]);
      setResourcesError(t('aiAgentsUi.knowledge.loadError', 'Impossible de charger les sources de cet agent.'));
    }
  }, [selectedAssistantId, agentIds, t]);

  // The account's sources, for the count per agent.
  const fetchCounts = useCallback(() => {
    axios.get('/api/files/list', { headers: authHeaders() })
      .then((response) => setCounts(countByAgent((response.data && response.data.files) || [])))
      .catch(() => setCounts(null));
  }, []);

  useEffect(() => {
    if (!user || !selectedAssistantId) return;
    setResources(null);
    fetchResources();
  }, [user, selectedAssistantId, fetchResources]);

  useEffect(() => {
    if (user && assistants && assistants.length) fetchCounts();
  }, [user, assistants, fetchCounts]);

  const refreshAfterChange = () => {
    fetchResources();
    fetchCounts();
  };

  // ---- adding -----------------------------------------------------------------

  const handleFileSelect = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';
    if (!selectedFiles.length) return;
    setUploadStatuses((previous) => {
      const next = { ...previous };
      selectedFiles.forEach((file) => { next[file.name] = { uploading: true, success: false }; });
      return next;
    });
    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append('files', file));
    try {
      const response = await axios.post(`/api/files/upload?assistant_id=${selectedAssistantId}`, formData, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      setUploadStatuses((previous) => {
        const next = { ...previous };
        (response.data.files || []).forEach((fileResult) => {
          next[fileResult.filename] = fileResult.status === 'success'
            ? { uploading: false, success: true }
            : { uploading: false, success: false, error: fileResult.message };
        });
        return next;
      });
      refreshAfterChange();
    } catch (error) {
      console.error('Upload failed:', error.response?.data || error.message);
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : t('aiAgentsUi.knowledge.uploadFailed', 'Échec de l’envoi.');
      setUploadStatuses((previous) => {
        const next = { ...previous };
        selectedFiles.forEach((file) => { next[file.name] = { uploading: false, success: false, error: message }; });
        return next;
      });
    }
  };

  const handleUrlFileSelect = async (event) => {
    const selectedFile = event.target.files?.[0];
    event.target.value = '';
    if (!selectedFile) return;
    setUploadStatuses((previous) => ({ ...previous, [selectedFile.name]: { uploading: true, success: false } }));
    const formData = new FormData();
    formData.append('file', selectedFile);
    try {
      await axios.post(`/api/files/upload-url-file?assistant_id=${selectedAssistantId}`, formData, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      setUploadStatuses((previous) => ({ ...previous, [selectedFile.name]: { uploading: false, success: true } }));
      refreshAfterChange();
    } catch (error) {
      console.error('Upload failed:', error.response?.data || error.message);
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : t('aiAgentsUi.knowledge.uploadFailed', 'Échec de l’envoi.');
      setUploadStatuses((previous) => ({ ...previous, [selectedFile.name]: { uploading: false, success: false, error: message } }));
    }
  };

  const openAddModal = () => {
    setUrl('');
    setUrlError(null);
    setShowAddModal(true);
  };

  const handleAddUrl = async (event) => {
    if (event) event.preventDefault();
    const value = url.trim();
    if (!value) {
      setUrlError(t('aiAgentsUi.knowledge.urlRequired', 'Saisissez l’URL de la page.'));
      return;
    }
    if (!/^https?:\/\/\S+/.test(value)) {
      setUrlError(t('knowledgeBoard.invalidUrl', 'L’URL doit commencer par http:// ou https://'));
      return;
    }
    setUrlError(null);
    setAddingUrl(true);
    try {
      await axios.put(
        `/api/files/update-web-json-2/?url=${encodeURIComponent(value)}&assistant_id=${selectedAssistantId}`,
        null,
        { headers: authHeaders() },
      );
      setShowAddModal(false);
      showToast('success', t('aiAgentsUi.knowledge.webAdded', 'Page web ajoutée aux connaissances.'));
      refreshAfterChange();
    } catch (error) {
      console.error('Failed to add web resource:', error);
      setShowAddModal(false);
      showToast('danger', t('aiAgentsUi.knowledge.webError', 'La page web n’a pas pu être ajoutée.'));
    } finally {
      setAddingUrl(false);
    }
  };

  const openAddAction = (action) => {
    setShowAddMenu(false);
    if (action === 'files' && fileInputRef.current) fileInputRef.current.click();
    else if (action === 'web') openAddModal();
    else if (action === 'urlFile' && urlFileInputRef.current) urlFileInputRef.current.click();
  };

  // ---- viewing and deleting -----------------------------------------------------

  const fetchAndViewResource = async (resource) => {
    try {
      const response = await axios.get(`/api/files/generate-view-url?file_key=${resource.id}`, { headers: authHeaders() });
      if (response.data.type === 'text') setViewer({ url: null, textContent: response.data.content, resource });
      else setViewer({ url: response.data.url, textContent: null, resource });
    } catch (error) {
      console.error('Failed to view resource:', error);
      showToast('danger', t('aiAgentsUi.knowledge.viewError', 'Impossible d’afficher cette source.'));
    }
  };

  const handleDelete = async () => {
    const resource = confirmDelete;
    setDeleting(true);
    try {
      const title = resource.title || resource.name;
      await axios.put(
        `/api/files/delete-web-json-2/?file_id=${encodeURIComponent(resource._id)}&title=${encodeURIComponent(title)}`,
        null,
        { headers: authHeaders() },
      );
      setResources((previous) => (previous || []).filter((entry) => entry._id !== resource._id));
      setConfirmDelete(null);
      showToast('success', t('aiAgentsUi.knowledge.deleted', '« {{title}} » retirée des connaissances.', { title }));
      fetchCounts();
    } catch (error) {
      console.error('Failed to remove resource entry:', error);
      setConfirmDelete(null);
      showToast('danger', t('aiAgentsUi.knowledge.deleteError', 'La suppression a échoué.'));
    } finally {
      setDeleting(false);
    }
  };

  // ---- derived ------------------------------------------------------------------

  const list = useMemo(() => resources || [], [resources]);
  const summary = useMemo(() => knowledgeSummary(list), [list]);
  const checked = list.filter((file) => indexStatus(file)).length;
  const needle = query.trim().toLowerCase();
  const visible = list.filter((resource) => matchesKnowledgeFilter(resource, filter) && (!needle
    || [resource.title, resource.name, resource.description].some((text) => String(text || '').toLowerCase().includes(needle))));
  const selectedAgent = (assistants || []).find((assistant) => assistant._id === selectedAssistantId);
  const totalAccount = counts ? Object.values(counts).reduce((acc, value) => acc + value, 0) : null;
  const noAgentCount = counts
    ? Object.entries(counts).filter(([key]) => !key || !agentIds.has(key)).reduce((acc, [, value]) => acc + value, 0)
    : null;
  const uploadEntries = Object.entries(uploadStatuses);
  const uploadsActive = uploadEntries.some(([, status]) => status.uploading);
  const unreadableFiles = list.filter((file) => UNREADABLE_STATUSES.includes(indexStatus(file)));

  const setFilter = (next) => updateParams({ filter: next === 'all' ? null : next });

  const filterLabels = {
    all: t('aiAgentsUi.knowledge.filters.all', 'Toutes'),
    pdf: 'PDF',
    text: t('aiAgentsUi.knowledge.filters.text', 'Textes et pages web'),
    other: t('aiAgentsUi.knowledge.filters.other', 'Autres fichiers'),
    unreadable: t('aiAgentsUi.knowledge.filters.unreadable', 'Illisibles'),
    nosummary: t('aiAgentsUi.knowledge.filters.nosummary', 'Sans résumé'),
  };

  const indexBadge = (resource) => {
    const state = resource.knowledge_index || {};
    switch (state.status) {
      case 'indexed':
      case 'current':
        return <Badge tone="success" dot>{t('aiAgentsUi.knowledge.index.indexed', 'Lue')}</Badge>;
      case 'unreadable':
        return <Badge tone="danger" dot>{t('aiAgentsUi.knowledge.index.unreadable', 'Illisible')}</Badge>;
      case 'no_text':
        return <Badge tone="danger" dot>{t('aiAgentsUi.knowledge.index.noText', 'Aucun texte')}</Badge>;
      case 'source_missing':
        return <Badge tone="danger" dot>{t('aiAgentsUi.knowledge.index.missing', 'Fichier introuvable')}</Badge>;
      case 'retry':
        return <Badge tone="warning" dot>{t('aiAgentsUi.knowledge.index.retry', 'Nouvel essai prévu')}</Badge>;
      case 'url_list_skipped':
        return <Badge>{t('aiAgentsUi.knowledge.index.urlList', 'Ancienne liste d’URL')}</Badge>;
      case 'agent_missing':
      case 'outside_tenant_folder':
        return <Badge>{t('aiAgentsUi.knowledge.index.skipped', 'Non relue')}</Badge>;
      default:
        return <span className="u-muted">{t('aiAgentsUi.knowledge.index.unknown', 'Pas encore vérifiée')}</span>;
    }
  };

  const indexSub = (resource) => {
    const state = resource.knowledge_index || {};
    if ((state.status === 'indexed' || state.status === 'current') && state.pieces) {
      return t('aiAgentsUi.knowledge.pieces', '{{count}} passages', { count: state.pieces });
    }
    if (UNREADABLE_STATUSES.includes(state.status)) return t('aiAgentsUi.knowledge.unreadableHint', 'l’agent ne peut pas la lire');
    return null;
  };

  // ---- gates --------------------------------------------------------------------

  if (user === undefined) {
    return (
      <div className="u-page kb-v2" aria-busy="true">
        <PageHeader title={t('aiAgentsUi.knowledge.title', 'Connaissances')} />
        <div className="u-well">
          <div className="u-kpi-row kb-v2__kpis" aria-hidden="true">
            {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton kb-v2__kpi-skeleton" />)}
          </div>
          <span className="u-skeleton kb-v2__card-skeleton" aria-hidden="true" />
        </div>
      </div>
    );
  }

  if (user === null) {
    return (
      <div className="u-page kb-v2">
        <PageHeader title={t('aiAgentsUi.knowledge.title', 'Connaissances')} />
        <Banner tone="danger">{t('knowledgeBoard.userFetchError', 'Impossible de charger votre profil.')}</Banner>
      </div>
    );
  }

  if (user.subscription_plan === 'free') {
    return (
      <div className="u-page kb-v2">
        <PageHeader title={t('aiAgentsUi.knowledge.title', 'Connaissances')} />
        <div className="u-well">
          <Card>
            <EmptyState
              icon={BookOpen}
              title={t('aiAgentsUi.knowledge.paidTitle', 'Réservé aux abonnements payants')}
              action={(
                <button type="button" className="u-btn u-btn--primary u-btn--sm" onClick={() => navigate('/Payment')}>
                  {t('knowledgeBoard.upgradeNow', 'Mettre à niveau')}
                </button>
              )}
            >
              {t('knowledgeBoard.noAccessMessage', 'La base de connaissances nécessite un abonnement payant.')}
            </EmptyState>
          </Card>
        </div>
      </div>
    );
  }

  const assistantsLoaded = assistants !== null;
  const hasAssistants = assistantsLoaded && assistants.length > 0;
  const loadingList = hasAssistants && resources === null;

  // ---- zones --------------------------------------------------------------------

  let subtitle;
  if (!hasAssistants) {
    subtitle = t('aiAgentsUi.knowledge.subtitleEmpty', 'Ce que vos agents consultent avant de répondre : documents et pages web.');
  } else if (noAgent && !loadingList) {
    subtitle = t('settingsPolishUi.knowledge.noAgentSubtitle', '{{count}} sources sans agent : ajoutées avant le rattachement par agent, ou d’un agent supprimé', { count: list.length });
  } else if (loadingList || !selectedAgent) {
    subtitle = t('aiAgentsUi.knowledge.subtitleLoading', 'Ce que l’agent consulte avant de répondre.');
  } else {
    subtitle = t('aiAgentsUi.knowledge.subtitle', '{{count}} sources pour « {{name}} » · titres et résumés écrits par l’IA à l’ajout', { count: list.length, name: selectedAgent.name });
  }

  const addMenu = hasAssistants && selectedAssistantId && !noAgent && (
    <div className="u-menu-anchor" ref={addMenuRef}>
      <button
        type="button"
        className="u-btn u-btn--primary"
        aria-haspopup="menu"
        aria-expanded={showAddMenu}
        onClick={() => setShowAddMenu((value) => !value)}
      >
        <Plus aria-hidden="true" />
        {t('knowledgeBoard.addSources', 'Ajouter des sources')}
        <ChevronDown aria-hidden="true" />
      </button>
      {showAddMenu && (
        <div className="u-menu kb-v2__add-menu" role="menu">
          <button type="button" role="menuitem" className="u-menu__item" onClick={() => openAddAction('files')}>
            <Upload strokeWidth={1.8} aria-hidden="true" />
            <span className="kb-v2__menu-text">
              {t('knowledgeBoard.addFiles', 'Fichiers')}
              <span className="kb-v2__menu-hint">{t('knowledgeBoard.addFilesHint', 'PDF, documents, texte')}</span>
            </span>
          </button>
          <button type="button" role="menuitem" className="u-menu__item" onClick={() => openAddAction('web')}>
            <Globe strokeWidth={1.8} aria-hidden="true" />
            <span className="kb-v2__menu-text">
              {t('knowledgeBoard.webPage', 'Page web')}
              <span className="kb-v2__menu-hint">{t('knowledgeBoard.addWebPageMenuHint', 'Lire et indexer une URL')}</span>
            </span>
          </button>
          <button type="button" role="menuitem" className="u-menu__item" onClick={() => openAddAction('urlFile')}>
            <LinkIcon strokeWidth={1.8} aria-hidden="true" />
            <span className="kb-v2__menu-text">
              {t('knowledgeBoard.addUrlList', 'Liste d’URLs')}
              <span className="kb-v2__menu-hint">{t('knowledgeBoard.addUrlListHint', 'Fichier texte, une URL par ligne')}</span>
            </span>
          </button>
        </div>
      )}
    </div>
  );

  const agentSelect = hasAssistants && (
    <label className="kb-v2__agent" htmlFor="kb-agent-select">
      <span className="kb-v2__agent-label">{t('knowledgeBoard.selectAgent', 'Agent')}</span>
      <select
        id="kb-agent-select"
        className="u-select kb-v2__agent-select"
        value={selectedAssistantId || ''}
        onChange={(e) => updateParams({ agent: e.target.value, filter: null, q: null })}
      >
        {assistants.map((assistant) => (
          <option key={assistant._id} value={assistant._id}>
            {assistant.name}
            {assistant.is_running ? ` ${t('aiAgentsUi.knowledge.defaultSuffix', '(par défaut)')}` : ''}
            {counts ? ` · ${formatCount(counts[assistant._id] || 0)}` : ''}
          </option>
        ))}
        {(noAgent || noAgentCount > 0) && (
          <option value={NO_AGENT}>
            {t('settingsPolishUi.knowledge.noAgent', 'Sans agent')}
            {noAgentCount !== null ? ` · ${formatCount(noAgentCount)}` : ''}
          </option>
        )}
      </select>
    </label>
  );

  const renderAttention = () => {
    if (!unreadableFiles.length) return null;
    return (
      <Card
        icon={TriangleAlert}
        iconTone="warning"
        title={t('aiAgentsUi.attention.title', 'À vérifier')}
        badge={<Badge tone="warning">1</Badge>}
        flush
        className="kb-v2__attention"
      >
        <ul className="u-list">
          <li className="u-row">
            <Badge tone="danger" dot>{t('aiAgentsUi.attention.blocking', 'Bloquant')}</Badge>
            <div className="u-row__main">
              <div className="u-row__title">{t('aiAgentsUi.knowledge.unreadableTitle', '{{count}} sources ne sont pas lisibles par l’agent', { count: unreadableFiles.length })}</div>
              <div className="u-row__meta" title={unreadableFiles.map((file) => file.title || file.name).join(', ')}>
                {t('aiAgentsUi.knowledge.unreadableDetail', 'Remplacez-les par une version texte ou un PDF non scanné : {{names}}', { names: unreadableFiles.map((file) => file.title || file.name).join(', ') })}
              </div>
            </div>
            <div className="u-row__side">
              <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => setFilter('unreadable')}>
                {t('aiAgentsUi.see', 'Voir')}
              </button>
            </div>
          </li>
        </ul>
      </Card>
    );
  };

  const renderKpis = () => {
    if (!hasAssistants) return null;
    if (loadingList) {
      return (
        <div className="u-kpi-row kb-v2__kpis" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton kb-v2__kpi-skeleton" />)}
        </div>
      );
    }
    if (!list.length) return null;
    return (
      <div className="u-kpi-row kb-v2__kpis">
        <Kpi
          icon={BookOpen}
          label={t('aiAgentsUi.knowledge.kpi.sources', 'Sources')}
          value={formatCount(summary.total)}
          context={summary.pieces
            ? t('aiAgentsUi.knowledge.kpi.pieces', '{{value}} passages lus par l’agent', { count: summary.pieces, value: formatCount(summary.pieces) })
            : totalAccount !== null
              ? t('aiAgentsUi.knowledge.kpi.account', '{{value}} sur le compte', { count: totalAccount, value: formatCount(totalAccount) })
              : noAgent
                ? t('settingsPolishUi.knowledge.noAgentKpi', 'rattachées à aucun agent')
                : t('aiAgentsUi.knowledge.kpi.forAgent', 'pour cet agent')}
          active={filter === 'all'}
          onClick={() => setFilter('all')}
        />
        <Kpi
          icon={FileText}
          label="PDF"
          value={formatCount(summary.pdf)}
          context={t('aiAgentsUi.knowledge.kpi.share', '{{share}} des sources', { share: formatPercent(summary.pdf / summary.total) })}
          meter={{ value: summary.pdf, max: summary.total, label: 'PDF' }}
          active={filter === 'pdf'}
          onClick={() => setFilter('pdf')}
        />
        <Kpi
          icon={Globe}
          label={t('aiAgentsUi.knowledge.filters.text', 'Textes et pages web')}
          value={formatCount(summary.text)}
          context={summary.other
            ? t('aiAgentsUi.knowledge.kpi.others', '{{count}} autres fichiers', { count: summary.other })
            : t('aiAgentsUi.knowledge.kpi.share', '{{share}} des sources', { share: formatPercent(summary.text / summary.total) })}
          meter={{ value: summary.text, max: summary.total, label: t('aiAgentsUi.knowledge.filters.text', 'Textes et pages web') }}
          active={filter === 'text'}
          onClick={() => setFilter('text')}
        />
        <Kpi
          icon={CircleAlert}
          tone={summary.unreadable ? 'danger' : checked ? 'success' : 'neutral'}
          label={t('aiAgentsUi.knowledge.filters.unreadable', 'Illisibles')}
          value={formatCount(summary.unreadable)}
          context={summary.unreadable
            ? t('aiAgentsUi.knowledge.kpi.unreadable', 'l’agent ne peut pas les lire')
            : checked
              ? t('aiAgentsUi.knowledge.kpi.checked', 'sur {{count}} vérifiées', { count: checked })
              : t('aiAgentsUi.knowledge.kpi.notChecked', 'pas encore vérifiées')}
          contextTone={summary.unreadable ? 'danger' : undefined}
          active={filter === 'unreadable'}
          onClick={() => setFilter('unreadable')}
        />
      </div>
    );
  };

  const renderUploads = () => {
    if (!uploadEntries.length) return null;
    return (
      <Card
        icon={Upload}
        title={t('aiAgentsUi.knowledge.uploads', 'Envois')}
        actions={!uploadsActive ? (
          <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => setUploadStatuses({})}>
            {t('knowledgeBoard.dismiss', 'Masquer')}
          </button>
        ) : null}
        flush
      >
        <ul className="u-list">
          {uploadEntries.map(([filename, status]) => (
            <li key={filename} className="u-row kb-v2__upload">
              <div className="u-row__main">
                <div className="u-row__title kb-v2__upload-name" title={filename}>{filename}</div>
                {!status.uploading && !status.success && status.error && <div className="u-row__meta u-row__meta--wrap">{status.error}</div>}
              </div>
              <div className="u-row__side">
                {status.uploading && <Badge tone="info" dot>{t('aiAgentsUi.knowledge.uploading', 'Envoi…')}</Badge>}
                {!status.uploading && status.success && <Badge tone="success" dot>{t('aiAgentsUi.knowledge.uploaded', 'Ajouté')}</Badge>}
                {!status.uploading && !status.success && <Badge tone="danger" dot>{t('aiAgentsUi.knowledge.uploadError', 'Échec')}</Badge>}
              </div>
            </li>
          ))}
        </ul>
      </Card>
    );
  };

  const renderTable = () => {
    if (!assistantsLoaded || loadingList) {
      return (
        <div className="kb-v2__skeleton" aria-busy="true">
          {Array.from({ length: 6 }, (_, i) => <span key={i} className="u-skeleton" />)}
        </div>
      );
    }
    if (!hasAssistants) {
      return (
        <EmptyState
          icon={BookOpen}
          title={t('aiAgentsUi.knowledge.noAgentsTitle', 'Aucun agent pour l’instant')}
          action={<Link className="u-btn u-btn--primary u-btn--sm" to="/my-ai-assistants">{t('knowledgeBoard.goToAgents', 'Voir les agents IA')}</Link>}
        >
          {t('knowledgeBoard.noAgents', 'Créez d’abord un agent IA : ses connaissances vivront ici.')}
        </EmptyState>
      );
    }
    if (!list.length) {
      if (resourcesError) return null;
      if (noAgent) {
        return (
          <EmptyState icon={BookOpen} title={t('settingsPolishUi.knowledge.noAgentEmpty', 'Aucune source sans agent')}>
            {t('settingsPolishUi.knowledge.noAgentEmptyHint', 'Toutes vos sources sont rattachées à un agent.')}
          </EmptyState>
        );
      }
      return (
        <EmptyState
          icon={BookOpen}
          title={t('aiAgentsUi.knowledge.emptyTitle', 'Aucune source pour cet agent')}
          action={(
            <span className="kb-v2__empty-actions">
              <button type="button" className="u-btn u-btn--primary u-btn--sm" onClick={() => openAddAction('files')}>
                <Upload aria-hidden="true" />
                {t('aiAgentsUi.knowledge.addFiles', 'Ajouter des fichiers')}
              </button>
              <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => openAddAction('web')}>
                <Globe aria-hidden="true" />
                {t('knowledgeBoard.addWebPage', 'Ajouter une page web')}
              </button>
            </span>
          )}
        >
          {t('aiAgentsUi.knowledge.emptyHint', 'Vos conditions, grilles et procédures : l’agent les consulte avant de répondre.')}
        </EmptyState>
      );
    }
    if (!visible.length) {
      return (
        <EmptyState
          icon={Search}
          title={t('knowledgeBoard.noMatches', 'Aucune source ne correspond à la recherche.')}
          action={(
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => updateParams({ q: null, filter: null })}>
              {t('aiAgentsUi.clearFilters', 'Effacer les filtres')}
            </button>
          )}
        />
      );
    }
    return (
      <div className="u-table-scroll">
        <table className="u-table kb-v2__table">
          <thead>
            <tr>
              <th style={{ width: '58%' }}>{t('aiAgentsUi.knowledge.colSource', 'Source')}</th>
              <th style={{ width: '12%' }}>{t('aiAgentsUi.knowledge.colType', 'Type')}</th>
              <th style={{ width: '22%' }}>{t('aiAgentsUi.knowledge.colReading', 'Lecture par l’agent')}</th>
              <th style={{ width: '8%' }} aria-label={t('aiAgentsUi.actions', 'Actions')} />
            </tr>
          </thead>
          <tbody>
            {visible.map((resource) => {
              const kind = fileKind(resource);
              const ext = fileExtension(resource);
              const KindIcon = KIND_ICON[kind];
              const title = resource.title || resource.name;
              const sub = indexSub(resource);
              return (
                <tr key={resource._id}>
                  <td title={title}>
                    <span className="kb-v2__source">
                      <IconChip icon={KindIcon} size="sm" tone={kind === 'pdf' ? undefined : 'neutral'} />
                      <span className="kb-v2__source-text">
                        <button type="button" className="kb-v2__title" onClick={() => fetchAndViewResource(resource)}>{title}</button>
                        <span className="u-cell-sub" title={resource.description || resource.name}>
                          {String(resource.description || '').trim() || (
                            <span className="kb-v2__no-summary">{t('aiAgentsUi.knowledge.noSummary', 'Sans résumé')} · {resource.name}</span>
                          )}
                        </span>
                      </span>
                    </span>
                  </td>
                  <td><span className="u-code">{ext || 'DOC'}</span></td>
                  <td>
                    {indexBadge(resource)}
                    {sub && <span className="u-cell-sub">{sub}</span>}
                  </td>
                  <td className="kb-v2__actions">
                    <OverflowMenu
                      label={t('aiAgentsUi.knowledge.actions', 'Actions de la source')}
                      items={[
                        { label: t('aiAgentsUi.knowledge.preview', 'Aperçu'), icon: Eye, onClick: () => fetchAndViewResource(resource) },
                        { separator: true },
                        { label: t('aiAgentsUi.knowledge.delete', 'Retirer des connaissances'), icon: Trash2, danger: true, onClick: () => setConfirmDelete(resource) },
                      ]}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const segmentOptions = SEGMENT_FILTERS.map((key) => ({
    value: key,
    label: filterLabels[key],
    count: resources ? formatCount(key === 'all' ? summary.total : summary[key]) : undefined,
  }));
  if (summary.noSummary > 0) {
    segmentOptions.push({ value: 'nosummary', label: filterLabels.nosummary, count: formatCount(summary.noSummary) });
  }

  return (
    <div className="u-page kb-v2">
      <PageHeader
        title={t('aiAgentsUi.knowledge.title', 'Connaissances')}
        subtitle={subtitle}
        actions={(
          <>
            {agentSelect}
            {addMenu}
          </>
        )}
      />

      <div className="u-well">
        {resourcesError && (
          <Banner tone="danger" actions={<button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={fetchResources}>{t('aiAgentsUi.retry', 'Réessayer')}</button>}>
            {resourcesError}
          </Banner>
        )}
        {renderAttention()}
        {renderKpis()}
        {renderUploads()}
        <section className="u-card">
          {hasAssistants && list.length > 0 && (
            <div className="u-toolbar">
              <div className="u-input-group u-input-group--lead">
                <Search className="u-input-group__icon" aria-hidden="true" />
                <input
                  type="search"
                  className="u-input"
                  value={query}
                  onChange={(e) => updateParams({ q: e.target.value })}
                  placeholder={t('aiAgentsUi.knowledge.search', 'Titre ou résumé')}
                  aria-label={t('common.search', 'Rechercher')}
                />
              </div>
              <Segmented
                label={t('aiAgentsUi.knowledge.filterLabel', 'Filtrer les sources')}
                value={filter}
                onChange={setFilter}
                options={segmentOptions}
              />
              {filter === 'unreadable' && (
                <span className="kb-v2__active-filter">
                  <Badge tone="accent">{filterLabels.unreadable}</Badge>
                  <button
                    type="button"
                    className="u-btn u-btn--ghost u-btn--icon u-btn--sm"
                    onClick={() => setFilter('all')}
                    aria-label={t('aiAgentsUi.clearFilter', 'Retirer le filtre')}
                    title={t('aiAgentsUi.clearFilter', 'Retirer le filtre')}
                  >
                    <X aria-hidden="true" />
                  </button>
                </span>
              )}
              <span className="u-toolbar__spacer" />
              <span className="kb-v2__count">{t('aiAgentsUi.shown', '{{count}} affichées', { count: visible.length })}</span>
            </div>
          )}
          {renderTable()}
        </section>
      </div>

      <input type="file" id="kbFileInput" ref={fileInputRef} multiple onChange={handleFileSelect} hidden />
      <input type="file" id="kbUrlFileInput" ref={urlFileInputRef} onChange={handleUrlFileSelect} hidden />

      {showAddModal && (
        <Modal
          icon={Globe}
          title={t('knowledgeBoard.addWebPage', 'Ajouter une page web')}
          description={t('knowledgeBoard.addWebPageHint', 'La page sera lue et indexée dans les connaissances de l’agent — le titre et le résumé sont générés automatiquement.')}
          onClose={() => !addingUrl && setShowAddModal(false)}
          closeLabel={t('common.close', 'Fermer')}
          footer={(
            <>
              <button type="button" className="u-btn u-btn--secondary" onClick={() => setShowAddModal(false)} disabled={addingUrl}>
                {t('common.cancel', 'Annuler')}
              </button>
              <button type="submit" form="kb-add-url" className="u-btn u-btn--primary" disabled={addingUrl}>
                {addingUrl ? t('knowledgeBoard.adding', 'Ajout en cours…') : t('knowledgeBoard.add', 'Ajouter')}
              </button>
            </>
          )}
        >
          <form id="kb-add-url" className="u-form" onSubmit={handleAddUrl} noValidate>
            <Field label={t('knowledgeBoard.resourceUrl', 'URL')} htmlFor="kb-res-url" error={urlError}>
              <input
                id="kb-res-url"
                className="u-input"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://…"
                aria-invalid={Boolean(urlError)}
                disabled={addingUrl}
                autoFocus
              />
            </Field>
          </form>
        </Modal>
      )}

      {viewer && (
        <Modal
          wide
          icon={viewer.textContent !== null ? FileText : File}
          title={viewer.resource.title || viewer.resource.name}
          description={viewer.resource.description || undefined}
          onClose={() => setViewer(null)}
          closeLabel={t('common.close', 'Fermer')}
          footer={viewer.url ? (
            <a href={viewer.url} target="_blank" rel="noopener noreferrer" className="u-btn u-btn--secondary">
              <ExternalLink aria-hidden="true" />
              {t('knowledgeBoard.openInNewTab', 'Ouvrir dans un onglet')}
            </a>
          ) : null}
        >
          {viewer.textContent !== null
            ? <pre className="kb-v2__viewer-text">{viewer.textContent}</pre>
            : <iframe src={viewer.url} title={viewer.resource.title || 'Resource'} className="kb-v2__viewer-frame" />}
        </Modal>
      )}

      {confirmDelete && (
        <ConfirmDialog
          title={t('aiAgentsUi.knowledge.deleteTitle', 'Retirer « {{title}} » des connaissances ?', { title: confirmDelete.title || confirmDelete.name })}
          message={t('aiAgentsUi.knowledge.deleteMessage', 'L’agent ne pourra plus s’en servir. Pour la retrouver, il faudra l’ajouter à nouveau.')}
          confirmLabel={t('aiAgentsUi.knowledge.deleteConfirm', 'Retirer')}
          cancelLabel={t('common.cancel', 'Annuler')}
          busy={deleting}
          icon={Trash2}
          onCancel={() => !deleting && setConfirmDelete(null)}
          onConfirm={handleDelete}
        />
      )}

      {toast && (
        <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')}>
          {toast.text}
        </Toast>
      )}
    </div>
  );
};

export default KnowledgeBoard;
